import { ForwardedRef, forwardRef, useMemo, useState } from "react";
import * as s from "../SignupStyled";
import { ModalInput } from "components/atoms/input/modal/ModalInput";
import {
  FreeTrialInfo,
  ResellerUserInfo,
  SIGNUP_UPDATE_TYPE,
  SignupUserInfo,
} from "api/interfaces/authInterface.interface";
import { ValidateMsg } from "api/interfaces/commonInterface.interface";
import { UnderlineText } from "components/atoms/text/Text";
import TableButton from "components/atoms/buttons/TableButton";
import Input from "components/atoms/input/Input";
import PhoneInput from "components/atoms/input/phone/PhoneInput";
import { BsQuestionCircleFill } from "react-icons/bs";
import InfoBubble, {
  BubbleContainer,
} from "components/blocks/bubble/InfoBubble";

interface Props {
  validateInputMsg: ValidateMsg;
  onChangeUserInfo: (type: SIGNUP_UPDATE_TYPE, data: string | boolean) => void;
  userInfo: SignupUserInfo | ResellerUserInfo | FreeTrialInfo;
  isPhoneVerified?: boolean;
  mfaNumber?: string;
  onClickResend?: () => void;
  onSendVerify?: () => void;
  onClickVerify?: () => void;
  onClickVerifyCancel?: () => void;
  verifyCode?: string;
  isValidVerify?: boolean;
  children?: React.ReactNode;
  requiredVerify?: boolean;
  onChangeVerifyCode?: (value: string) => void;
  tabIndex?: number;
  essential?: boolean;
}

function Phone(props: Props, ref?: ForwardedRef<HTMLInputElement>) {
  const {
    validateInputMsg,
    onChangeUserInfo,
    userInfo,
    isPhoneVerified,
    mfaNumber,
    onClickResend,
    onSendVerify,
    onClickVerify,
    onClickVerifyCancel,
    verifyCode,
    isValidVerify,
    children,
    requiredVerify,
    onChangeVerifyCode,
    tabIndex,
    essential,
  } = props;
  const [isMouseOver, setIsMouseOver] = useState(false);
  const phoneNumber = useMemo(() => {
    if ("phone" in userInfo && typeof userInfo.phone === "string") {
      return userInfo.phone;
    }
    if ("phoneNumber" in userInfo && userInfo.phoneNumber) {
      return userInfo.phoneNumber;
    }
    return "";
  }, [userInfo]);
  return (
    <ModalInput
      label="Phone Number"
      noBold
      isValidated={!!validateInputMsg.phoneNumber}
      validatedWord={validateInputMsg.phoneNumber}
      essential={essential}
    >
      <s.PhoneWrapper>
        <div className="phone-input-wrapper">
          <PhoneInput
            login
            // selected={props.isFlagSelected}
            // onSelect={props.onSelectFlag}
            selected={phoneNumber}
            placeholder={`${
              requiredVerify ? "P" : "Please enter a p"
            }hone number${essential ? " (Required)" : " (Optional)"}`}
            onChange={(value: string) => {
              onChangeUserInfo(SIGNUP_UPDATE_TYPE.PHONE_NUMBER, value);
            }}
            // onEnterKeyPress={() => console.log("on enter")}
            isValidated={!!validateInputMsg.phoneNumber}
            disabled={isPhoneVerified}
            tabIndex={tabIndex}
            data-testid="auth-signup-common-phone-input-change-user-info"
          />

          {requiredVerify && (
            <TableButton
              onClick={mfaNumber !== "" ? onClickResend : onSendVerify}
              disabled={isPhoneVerified}
              label={mfaNumber !== "" ? "Resend Code" : "Send Code"}
              buttonType="primary"
              icon={
                mfaNumber === "" && (
                  <button
                    className="icon-button"
                    onMouseEnter={() => {
                      setIsMouseOver(true);
                    }}
                    onMouseLeave={() => {
                      setIsMouseOver(false);
                    }}
                  >
                    <BsQuestionCircleFill size={12} />
                    {isMouseOver && (
                      <BubbleContainer>
                        <InfoBubble>Verify it’s you</InfoBubble>
                      </BubbleContainer>
                    )}
                  </button>
                )
              }
              data-testid="auth-signup-common-table-button-click"
            />
          )}
        </div>
        {isPhoneVerified && requiredVerify && (
          <div style={{ width: "100%" }}>
            <UnderlineText
              id="dw_signup_button_forward_signup"
              onClick={onClickVerifyCancel}
              fontSize={13}
              data-testid="auth-signup-common-forward-signup-click-verify-cancel"
            >
              Reset Phone Number
            </UnderlineText>
          </div>
        )}

        {mfaNumber !== "" && !isPhoneVerified && requiredVerify && (
          <s.ValidWrapper>
            <div className="phone-input-wrapper">
              <Input
                value={verifyCode}
                onChange={(e) => onChangeVerifyCode?.(e.target.value)}
                // onKeyDown={onClickVerify}
                maxLength={6}
                isValidated={isValidVerify}
                placeholder="Verification Code"
                tabIndex={(tabIndex || 0) + 1}
                data-testid="auth-signup-common-input-change"
              />

              <TableButton
                onClick={onClickVerify}
                label={"Verify"}
                buttonType="primary"
                data-testid="auth-signup-common-table-button-click-verify"
              />
            </div>
          </s.ValidWrapper>
        )}
      </s.PhoneWrapper>
      {children}
    </ModalInput>
  );
}

export default forwardRef(Phone);
